import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { WompiReportsService, ReportConciliationLogDTO } from '../../../../services/wompi-reports.service';
import { ToastService } from '../../../../services/toast.service';
import { LoadingService } from '../../../../services/loading.service';

interface ConciliatedPayment {
  PaymentPlanId?: number;
  IncomeReceiptId?: number;
  PaymentLinkId?: string;
  GrossAmount?: number;
  NetAmount?: number;
  TotalFees?: number;
  ReconciliationId?: string;
  ConciliatedAt?: string;
  CommissionValue?: number;
  ReteFuenteValue?: number;
  ReteIVAValue?: number;
  ReteICAValue?: number;
  ProvisionValue?: number;
  // camelCase alternatives
  paymentPlanId?: number;
  incomeReceiptId?: number;
  paymentLinkId?: string;
  grossAmount?: number;
  netAmount?: number;
  totalFees?: number;
  reconciliationId?: string;
  conciliatedAt?: string;
  commissionValue?: number;
  reteFuenteValue?: number;
  reteIVAValue?: number;
  reteICAValue?: number;
  provisionValue?: number;
}

@Component({
  selector: 'app-conciliation-log-detail',
  templateUrl: './conciliation-log-detail.component.html',
  styleUrls: ['./conciliation-log-detail.component.scss']
})
export class ConciliationLogDetailComponent implements OnInit {
  logId: number = 0;
  log: ReportConciliationLogDTO | null = null;
  payments: ConciliatedPayment[] = [];
  loading: boolean = false;

  // Pagination
  currentPage: number = 1;
  pageSize: number = 20;
  totalPages: number = 1;
  
  // Make Math available in template
  Math = Math;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private wompiReportsService: WompiReportsService,
    private toastService: ToastService,
    private loadingService: LoadingService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.logId = +params['id'];
      this.loadLogDetail();
    });
  }

  loadLogDetail(): void {
    this.loading = true;
    this.loadingService.show();

    this.wompiReportsService.getConciliationLogById(this.logId).subscribe({
      next: (log) => {
        this.log = log;
        this.parsePayments();
        this.loading = false;
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error loading log detail:', error);
        this.toastService.showToast('error', 'Error al cargar el detalle del log');
        this.loading = false;
        this.loadingService.hide();
        this.router.navigate(['/dashboard/config/conciliation-logs']);
      }
    });
  }

  parsePayments(): void {
    if (!this.log?.conciliatedPaymentsSummary) {
      this.payments = [];
      return;
    }
    try {
      this.payments = JSON.parse(this.log.conciliatedPaymentsSummary);
      this.totalPages = Math.ceil(this.payments.length / this.pageSize);
    } catch {
      this.payments = [];
    }
  }

  get paginatedPayments(): ConciliatedPayment[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.payments.slice(start, start + this.pageSize);
  }

  goBack(): void {
    this.router.navigate(['/dashboard/config/conciliation-logs']);
  }

  viewIncomeReceipt(payment: ConciliatedPayment): void {
    const receiptId = payment.IncomeReceiptId || payment.incomeReceiptId;
    if (receiptId) {
      this.router.navigate(['/dashboard/income-receipts/view', receiptId]);
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  getStatusBadgeClasses(status: number): string {
    switch (status) {
      case 0: return 'bg-gray-100 text-gray-800';
      case 1: return 'bg-blue-100 text-blue-800';
      case 2: return 'bg-green-100 text-green-800';
      case 3: return 'bg-amber-100 text-amber-800';
      case 4: return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CO', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }

  // Helper methods to get values with fallback for both naming conventions
  getIncomeReceiptId(p: ConciliatedPayment): number | undefined {
    return p.IncomeReceiptId || p.incomeReceiptId;
  }
  getGrossAmount(p: ConciliatedPayment): number {
    return p.GrossAmount || p.grossAmount || 0;
  }
  getNetAmount(p: ConciliatedPayment): number {
    return p.NetAmount || p.netAmount || 0;
  }
  getTotalFees(p: ConciliatedPayment): number {
    return p.TotalFees || p.totalFees || 0;
  }
  getReconciliationId(p: ConciliatedPayment): string {
    return p.ReconciliationId || p.reconciliationId || '-';
  }
  getCommissionValue(p: ConciliatedPayment): number {
    return Math.abs(p.CommissionValue || p.commissionValue || 0);
  }
  getReteFuenteValue(p: ConciliatedPayment): number {
    return Math.abs(p.ReteFuenteValue || p.reteFuenteValue || 0);
  }
  getReteIVAValue(p: ConciliatedPayment): number {
    return Math.abs(p.ReteIVAValue || p.reteIVAValue || 0);
  }
  getReteICAValue(p: ConciliatedPayment): number {
    return Math.abs(p.ReteICAValue || p.reteICAValue || 0);
  }
  getProvisionValue(p: ConciliatedPayment): number {
    return Math.abs(p.ProvisionValue || p.provisionValue || 0);
  }
  getConciliatedAt(p: ConciliatedPayment): string {
    return p.ConciliatedAt || p.conciliatedAt || '';
  }
}

